import React from 'react';
import theme from './ResidentialUnitEdit.module.scss';
import { Trans } from '@lingui/react/macro';
import { PricingFieldValue, OptionGroupDto, LinkingType } from '@/types';
import Banner from '@/components/Banner';
import { faArrowRight, faLevelUp } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { OptionGroupAccordion } from '../../options/OptionGroupAccordion/OptionGroupAccordion';
import { RoomPill } from '../../options/Rooms';
import { useRoomMap } from '@/services/rooms';
import { useGetActiveProject } from '@/utils/hooks';
import { LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';

export type ExtendedPricingFieldValue = PricingFieldValue & {
    oldValue: number;
};

export interface ResidentialUnitEditPricingFieldsConfirmProps {
    changedPricingFields: ExtendedPricingFieldValue[];
    optionGroups: OptionGroupDto[];
}

const ResidentialUnitEditPricingFieldsConfirm: React.FC<ResidentialUnitEditPricingFieldsConfirmProps> = ({
    changedPricingFields,
    optionGroups,
}) => {
    const pricingFieldsNames = changedPricingFields.map((pf) => `'${pf.name}'`).join(', ');
    const pricingFieldIds = changedPricingFields.map((pf) => pf.pricingFieldId);
    const project = useGetActiveProject();
    const roomsMap = useRoomMap(project?.id || null);

    return (
        <>
            <p>
                <Trans>You are about to change the following pricing variables - {pricingFieldsNames}</Trans>
            </p>
            <Banner type="info">
                <Trans>
                    <strong>Please note</strong> below are the options connected to {pricingFieldsNames}. Changing a variable
                    value change also all pricing of the options connected to it. You will also be able to update the orders
                    if ones were already created for these options.
                </Trans>
            </Banner>
            {!roomsMap ? (
                <LoadingIndicator />
            ) : (
                <div className={theme.listWrapper}>
                    {optionGroups.map((og) => {
                        const roomsWithChangedPricing =
                            og.linkType === LinkingType.Room
                                ? og.linkedRooms.filter(
                                      (r) => r.pricingFieldId && pricingFieldIds.includes(r.pricingFieldId)
                                  )
                                : [];

                        const directPricingField =
                            og.linkType === LinkingType.Direct && og.pricingField.id
                                ? changedPricingFields.find((pf) => pf.pricingFieldId === og.pricingField.id)
                                : null;

                        return (
                            <div key={og.id}>
                                <OptionGroupAccordion accordionVariant="bordered" readonly optionGroup={og} />
                                {roomsWithChangedPricing.map((r) => {
                                    const pricingField = changedPricingFields.find(
                                        (pf) => pf.pricingFieldId === r.pricingFieldId
                                    )!;

                                    return (
                                        <div key={r.roomId} className={theme.extraListInfo}>
                                            <RoomPill room={roomsMap.get(r.roomId)!} />
                                            <FontAwesomeIcon icon={faArrowRight} />
                                            <Banner icon={null} type="info">
                                                <Trans>
                                                    <strong>{pricingField.name}</strong> is changed from{' '}
                                                    <strong>
                                                        {pricingField.oldValue} to {pricingField.value}
                                                    </strong>
                                                </Trans>
                                            </Banner>
                                        </div>
                                    );
                                })}

                                {directPricingField && (
                                    <div className={theme.extraListInfo}>
                                        <FontAwesomeIcon rotation={90} icon={faLevelUp} />
                                        <Banner icon={null} type="info">
                                            <Trans>
                                                <strong>{directPricingField.name}</strong> is changed from{' '}
                                                <strong>
                                                    {directPricingField.oldValue} to {directPricingField.value}
                                                </strong>
                                            </Trans>
                                        </Banner>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </>
    );
};

export default ResidentialUnitEditPricingFieldsConfirm;
